import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  BRAND_ALTERNATE_NAMES,
  getBrandFaqSchemaEntities,
} from '../../data/brandSeoCopy';

const SITE_URL = 'https://www.elodiecarstensen.com';

const BRAND_DESCRIPTION =
  'Elodie Carstensen is a Berlin-based demi-couture fashion label and studio where neo-romanticism meets futurism, creating made-to-order collections and collaborative performances.';

/** Organization + Person + WebSite as one JSON-LD graph for entity clarity (homepage, about, brand). */
export const buildBrandIdentityStructuredData = (extraGraphNodes = []) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'Elodie Carstensen',
      alternateName: [
        ...new Set([
          'Elodie Carstensen demi-couture',
          'Elodie Carstensen fashion label',
          ...BRAND_ALTERNATE_NAMES,
        ]),
      ],
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-white-on-black-512.png`,
        contentUrl: `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-white-on-black-512.png`,
        encodingFormat: 'image/png',
        width: 512,
        height: 512,
        name: 'Elodie Carstensen crown monogram — white on black',
      },
      image: `${SITE_URL}/social-preview.jpg`,
      description: BRAND_DESCRIPTION,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Kadiner Str. 20A',
        postalCode: '10243',
        addressLocality: 'Berlin',
        addressCountry: 'DE',
      },
      email: 'info@elodiecarstensen.com',
      sameAs: ['https://www.instagram.com/elodiecarstensen/'],
      founder: { '@id': `${SITE_URL}/#person` },
    },
    {
      '@type': 'Person',
      '@id': `${SITE_URL}/#person`,
      name: 'Elodie Carstensen',
      url: SITE_URL,
      jobTitle: 'Fashion Designer & Artist',
      worksFor: { '@id': `${SITE_URL}/#organization` },
      description: BRAND_DESCRIPTION,
      image: `${SITE_URL}/social-preview.jpg`,
      sameAs: ['https://www.instagram.com/elodiecarstensen/'],
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Kadiner Str. 20A',
        postalCode: '10243',
        addressLocality: 'Berlin',
        addressCountry: 'DE',
      },
    },
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      url: SITE_URL,
      name: 'Elodie Carstensen',
      publisher: { '@id': `${SITE_URL}/#organization` },
      inLanguage: 'en',
    },
    ...extraGraphNodes,
  ],
});

// Default meta data that can be used across the site
export const defaultMetaData = {
  title: "Elodie Carstensen - Fashion Designer & Artist",
  description: "Elodie Carstensen is a Berlin‑based demi‑couture label and laboratory where neo‑romanticism meets futurism, creating made‑to‑order collections and collaborative, immersive performances.",
  image: "https://www.elodiecarstensen.com/social-preview.jpg",
  url: "https://www.elodiecarstensen.com",
  type: "website",
  structuredData: buildBrandIdentityStructuredData(),
};

const MetaTags = React.memo(({
  title = defaultMetaData.title,
  description = defaultMetaData.description,
  image = defaultMetaData.image,
  url = defaultMetaData.url,
  type = defaultMetaData.type,
  structuredData = defaultMetaData.structuredData,
  children
}) => {
  // Minimal validation logging (errors only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // Only log if critical meta data is missing
      if (!title || !description) {
        console.warn('MetaTags: Missing critical meta data', { title: !!title, description: !!description });
      }
    }
  }, [title, description]);

  return (
    <Helmet prioritizeSeoTags>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} data-rh="true" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} data-rh="true" />
      <meta property="og:url" content={url} data-rh="true" />
      <meta property="og:title" content={title} data-rh="true" />
      <meta property="og:description" content={description} data-rh="true" />
      <meta property="og:image" content={image} data-rh="true" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" data-rh="true" />
      <meta property="twitter:domain" content="elodiecarstensen.com" data-rh="true" />
      <meta property="twitter:url" content={url} data-rh="true" />
      <meta name="twitter:title" content={title} data-rh="true" />
      <meta name="twitter:description" content={description} data-rh="true" />
      <meta name="twitter:image" content={image} data-rh="true" />

      {/* Structured Data */}
      <script type="application/ld+json" data-rh="true">
        {JSON.stringify(structuredData)}
      </script>

      {/* Allow for additional custom meta tags */}
      {children}
    </Helmet>
  );
});

// Display name for debugging
MetaTags.displayName = 'MetaTags';

// Helper functions for specific page types
export const getProjectMetaData = (project) => {
  if (!project) {
    return defaultMetaData;
  }

  const description = project.description || project.images?.[0]?.text ||
    `${project.name} - A fashion collection by Elodie Carstensen`;

  const imageUrl = project.images?.[0]?.src ?
    `https://www.elodiecarstensen.com/assets/images/${project.images[0].src}` :
    defaultMetaData.image;

  return {
    title: `${project.name} by Elodie Carstensen - ${project.season}`,
    description,
    image: imageUrl,
    url: `https://www.elodiecarstensen.com/gallery/${project.id}`,
    type: "article",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      "name": project.name,
      "creator": {
        "@type": "Person",
        "name": "Elodie Carstensen"
      },
      "description": description,
      "image": imageUrl,
      "datePublished": project.season
    }
  };
};

export const getEventMetaData = (event) => {
  if (!event) {
    return defaultMetaData;
  }

  // Generate plain text description for meta tags
  let plainDescription = defaultMetaData.description; // Start with default
  if (event.description) {
    const paragraphs = event.description.split('\\n\\n'); // Split by double newline
    const meaningfulParagraph = paragraphs.find(p =>
      p.trim().length > 0 &&
      !p.trim().startsWith('#') &&
      !p.trim().startsWith('*') &&
      !p.trim().startsWith('-')
    );

    if (meaningfulParagraph) {
      plainDescription = meaningfulParagraph
        .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove markdown links (keep text)
        .replace(/[*_#`]/g, '')          // Remove basic markdown chars
        .replace(/\s+/g, ' ')         // Normalize whitespace
        .trim();

      // Truncate to ~160 chars
      if (plainDescription.length > 160) {
        plainDescription = plainDescription.substring(0, 157) + '...';
      }
    }
    // If no suitable paragraph is found, it keeps the default.
  }

  // Handle both new optimized image format (jpeg) and legacy format (path)
  const imagePath = event.preview_images?.[0]?.jpeg || event.preview_images?.[0]?.path;
  const imageUrl = imagePath ?
    `https://www.elodiecarstensen.com/assets/images/${imagePath}` :
    defaultMetaData.image;

  return {
    title: `${event.title} - Elodie Carstensen`,
    description: plainDescription,
    image: imageUrl,
    url: `https://www.elodiecarstensen.com/events/${event.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    type: "article",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Event",
      "name": event.title,
      "description": plainDescription,
      "startDate": event.date,
      "endDate": event.date,
      "location": {
        "@type": "Place",
        "name": event.location,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": event.location
        }
      },
      "organizer": {
        "@type": "Person",
        "name": "Elodie Carstensen",
        "url": "https://www.elodiecarstensen.com"
      },
      "image": imageUrl
    }
  };
};

export const getAboutMetaData = () => {
  return {
    ...defaultMetaData,
    title: "About Elodie Carstensen - Fashion Designer & Artist",
    description:
      "Immersive About timeline: events, press, and bio. For citable brand facts, crown monogram, and FAQ see elodiecarstensen.com/brand.",
    url: "https://www.elodiecarstensen.com/about",
    type: "profile",
    structuredData: buildBrandIdentityStructuredData([
      {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/about#webpage`,
        url: `${SITE_URL}/about`,
        name: 'About Elodie Carstensen — timeline, events, and press',
        description:
          'Immersive about experience with biography, exhibitions, and press. Canonical brand entity copy and logo downloads: /brand.',
        isPartOf: { '@id': `${SITE_URL}/#website` },
        about: { '@id': `${SITE_URL}/#organization` },
        significantLink: [`${SITE_URL}/brand`, `${SITE_URL}/about/cv`],
      },
    ]),
  };
};

/** Use on legal/info subpages so Organization graph is not duplicated on every route. */
export const getLegalPageStructuredData = (pageTitle, pageUrl) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: `${pageTitle} - Elodie Carstensen`,
  url: pageUrl,
  isPartOf: {
    '@type': 'WebSite',
    url: SITE_URL,
    name: 'Elodie Carstensen',
  },
});

/** Collection / project carousel pages: WebSite publisher without duplicating full Organization JSON-LD. */
export const getCollectionPageStructuredData = (projectName, projectId, description, imageUrl) => {
  const page = {
    '@type': 'WebPage',
    name: `${projectName} - Elodie Carstensen`,
    url: `${SITE_URL}/${projectId}`,
    description,
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      url: SITE_URL,
      name: 'Elodie Carstensen',
      publisher: {
        '@type': 'Organization',
        name: 'Elodie Carstensen',
        url: SITE_URL,
      },
    },
  };
  if (imageUrl) {
    page.primaryImageOfPage = { '@type': 'ImageObject', url: imageUrl, contentUrl: imageUrl };
  }
  return {
    '@context': 'https://schema.org',
    ...page,
  };
};

export const getBrandPageMetaData = () => ({
  title: 'Elodie Carstensen — brand, logo & about (official)',
  description:
    'Official brand hub: who Elodie Carstensen is, the crown monogram, authentic label checks, downloads, press-style facts, and FAQ. Berlin demi-couture. Links to the immersive About timeline.',
  image: `${SITE_URL}/assets/brand/elodie-carstensen-brand-og-1200x630.png`,
  url: `${SITE_URL}/brand`,
  type: 'article',
  structuredData: buildBrandIdentityStructuredData([
    {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/brand#webpage`,
      url: `${SITE_URL}/brand`,
      name: 'Elodie Carstensen — official brand, crown monogram, and about',
      description:
        'Canonical public page for the Elodie Carstensen fashion label: entity facts, crown monogram assets, label authentication, founder, downloads, and FAQ. Complements the immersive About experience at /about.',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#organization` },
      significantLink: [`${SITE_URL}/about`, `${SITE_URL}/about/cv`],
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/assets/brand/elodie-carstensen-brand-og-1200x630.png`,
        contentUrl: `${SITE_URL}/assets/brand/elodie-carstensen-brand-og-1200x630.png`,
        width: 1200,
        height: 630,
        name: 'Elodie Carstensen brand preview — crown monogram on dark background',
      },
    },
    {
      '@type': 'FAQPage',
      '@id': `${SITE_URL}/brand#faq`,
      mainEntity: getBrandFaqSchemaEntities(),
    },
    {
      '@type': 'ImageObject',
      '@id': `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-white-on-black.svg#image`,
      url: `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-white-on-black.svg`,
      contentUrl: `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-white-on-black.svg`,
      encodingFormat: 'image/svg+xml',
      name: 'Official Elodie Carstensen crown monogram — white on black (SVG)',
      description:
        'Vector download of the official Elodie Carstensen crown monogram for press and partners.',
      copyrightHolder: { '@id': `${SITE_URL}/#organization` },
      creator: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@type': 'ImageObject',
      '@id': `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-black-on-white.svg#image`,
      url: `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-black-on-white.svg`,
      contentUrl: `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-black-on-white.svg`,
      encodingFormat: 'image/svg+xml',
      name: 'Official Elodie Carstensen crown monogram — black on white (SVG)',
      description:
        'Vector download of the crown monogram reversed for light backgrounds.',
      copyrightHolder: { '@id': `${SITE_URL}/#organization` },
      creator: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@type': 'ImageObject',
      '@id': `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-white-on-black-512.png#image`,
      url: `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-white-on-black-512.png`,
      contentUrl: `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-white-on-black-512.png`,
      encodingFormat: 'image/png',
      width: 512,
      height: 512,
      name: 'Official Elodie Carstensen crown monogram — white on black (PNG 512)',
      copyrightHolder: { '@id': `${SITE_URL}/#organization` },
      creator: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@type': 'ImageObject',
      '@id': `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-white-on-black-1024.png#image`,
      url: `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-white-on-black-1024.png`,
      contentUrl: `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-white-on-black-1024.png`,
      encodingFormat: 'image/png',
      width: 1024,
      height: 1024,
      name: 'Official Elodie Carstensen crown monogram — white on black (PNG 1024)',
      copyrightHolder: { '@id': `${SITE_URL}/#organization` },
      creator: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@type': 'ImageObject',
      '@id': `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-black-on-white-512.png#image`,
      url: `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-black-on-white-512.png`,
      contentUrl: `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-black-on-white-512.png`,
      encodingFormat: 'image/png',
      width: 512,
      height: 512,
      name: 'Official Elodie Carstensen crown monogram — black on white (PNG 512)',
      copyrightHolder: { '@id': `${SITE_URL}/#organization` },
      creator: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@type': 'ImageObject',
      '@id': `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-black-on-white-1024.png#image`,
      url: `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-black-on-white-1024.png`,
      contentUrl: `${SITE_URL}/assets/brand/elodie-carstensen-crown-monogram-black-on-white-1024.png`,
      encodingFormat: 'image/png',
      width: 1024,
      height: 1024,
      name: 'Official Elodie Carstensen crown monogram — black on white (PNG 1024)',
      copyrightHolder: { '@id': `${SITE_URL}/#organization` },
      creator: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@type': 'ImageObject',
      '@id': `${SITE_URL}/assets/brand/elodie-carstensen-wordmark-white-on-dark-1200w.png#image`,
      url: `${SITE_URL}/assets/brand/elodie-carstensen-wordmark-white-on-dark-1200w.png`,
      contentUrl: `${SITE_URL}/assets/brand/elodie-carstensen-wordmark-white-on-dark-1200w.png`,
      encodingFormat: 'image/png',
      width: 1200,
      height: 320,
      name: 'Elodie Carstensen wordmark — white on dark (PNG, from SVG)',
      description: 'Horizontal wordmark derived from the official logo.svg for press and retailers.',
      copyrightHolder: { '@id': `${SITE_URL}/#organization` },
      creator: { '@id': `${SITE_URL}/#organization` },
    },
  ]),
});

export default MetaTags;
